import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Badge as BadgeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  DriveEta as DriveEtaIcon,
  Cake as CakeIcon,
  ContactPhone as ContactPhoneIcon,
  AccountBalanceWallet as WalletIcon,
  EventAvailable as EventIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import Avatar from '../../../components/common/Avatar';
import Navigation from '../../../components/portal/Navigation';
import { API_BASE_URL } from '../../../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { apiService } from '../../../services/api';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchStudentPaymentDetailsForTeacher, verifyPayment } from '../../../store/slices/paymentSlice';
import { StudentPaymentDetails, PaymentStatus } from '../../../types/portal';
import StarRating from '../../../components/common/StarRating';
import AdvancedLessonCard from '../../../components/portal/AdvancedLessonCard';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;
const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 1.5rem);
  padding-top: 20px;

  @media (max-width: 768px) {
    padding: 0.75rem var(--mobile-page-padding-x);
    padding-top: 16px;
  }
`;
const ContentCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  margin-bottom: 0.75rem;
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 0.5rem;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  @media (max-width: 968px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ProfileCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px solid rgba(99, 102, 241, 0.1);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
  min-width: 200px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0.75rem;
    min-width: 100%;
  }
`;

const ProfileText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;

  .name {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 900;
    color: #111827;
    letter-spacing: -0.04em;
    line-height: 1.1;
  }

  .email {
    font-size: 0.8rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
  }

  .role-badge {
    font-size: 0.6rem;
    font-weight: 700;
    padding: 0.2rem 0.6rem;
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const ProfileAvatarWrapper = styled.div`
  position: relative;
  padding: 4px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 6px;
    right: 6px;
    width: 16px;
    height: 16px;
    background: #10b981;
    border: 3px solid #fff;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  flex: 1;
  max-width: 800px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 968px) {
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: #ffffff;
  padding: 0.75rem 0.85rem;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 0.6rem 0.75rem;
    border-radius: 10px;
    gap: 0.6rem;
  }

  &:hover {
    border-color: rgba(99, 102, 241, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
    transform: translateY(-2px);
  }

  .icon-wrapper {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    color: #6366f1;
    flex-shrink: 0;
    
    svg {
      font-size: 1.1rem;
    }

    @media (max-width: 768px) {
      width: 24px;
      height: 24px;
      svg { font-size: 1rem; }
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
    min-width: 0;
  }

  .label {
    font-size: 0.55rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .value {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
`;

const SectionTitle = styled.h4.attrs((props) => ({ id: props.id }))`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.1rem;
  font-weight: 800;
  color: #222;
  margin: 1.5rem 0 0.75rem;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 1.25rem 0 0.75rem;
  }

  &:first-of-type {
    margin-top: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
    margin-bottom: 1.25rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .label {
    font-size: 0.65rem;
    font-weight: 800;
    color: #888;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #222;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;


const TableTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  .count {
    background: #f3f4f6;
    color: #6b7280;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-size: 0.75rem;
  }
`;



const SummaryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    padding: 0.75rem;
  }
`;

const SummaryCard = styled.div<{ $type?: 'total' | 'paid' | 'remaining' }>`
  background: #ffffff;
  padding: 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    
    ${props => props.$type === 'total' && `
      background: #eff6ff;
      color: #3b82f6;
    `}
    ${props => props.$type === 'paid' && `
      background: #ecfdf5;
      color: #10b981;
    `}
    ${props => props.$type === 'remaining' && `
      background: #fff7ed;
      color: #f59e0b;
    `}
  }

  .content {
    display: flex;
    flex-direction: column;
    
    .label {
      font-size: 0.7rem;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.025em;
    }

    .value {
      font-size: 1rem;
      font-weight: 900;
      color: #1e293b;
    }
  }
`;

const TeacherStudentDetail: React.FC = () => {
  const { t } = useTranslation();
  const { studentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { paymentDetails, isLoading: paymentsLoading } = useAppSelector((state) => state.payment);

  const [student, setStudent] = React.useState<any | null>(null);
  const [lessons, setLessons] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [verifyingPayment, setVerifyingPayment] = React.useState<string | null>(null);

  const [toast, setToast] = React.useState<string | null>(null);

  const { user: me } = useAppSelector((state) => state.auth);

  const handleVerifyPayment = async (paymentId: string, status: PaymentStatus) => {
    if (!me?.id) return;
    try {
      setVerifyingPayment(paymentId);
      const resultAction = await dispatch(verifyPayment({ 
        paymentId, 
        status, 
        verifiedBy: String(me.id) 
      }));
      
      if (verifyPayment.fulfilled.match(resultAction)) {
        setToast(`Payment ${status.toLowerCase()} successfully`);
        setTimeout(() => setToast(null), 3000);
        if (studentId) {
          dispatch(fetchStudentPaymentDetailsForTeacher(studentId));
        }
      } else {
        setError(String(resultAction.payload || 'Failed to update payment status'));
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setVerifyingPayment(null);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  };

  const formatDuration = (minutes: number) => {
    if (!minutes) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours} ${hours === 1 ? 'Hour' : 'Hours'} ${mins}m`;
    if (hours > 0) return `${hours} ${hours === 1 ? 'Hour' : 'Hours'}`;
    return `${mins}m`;
  };

  const unifiedList = React.useMemo(() => {
    const now = Date.now();
    
    // 1. Get all scheduled items (Future)
    const scheduledLessons = lessons
      .filter(l => {
        const status = String(l.status || '').toUpperCase();
        const isFuture = l.dateTime ? new Date(l.dateTime).getTime() > now : false;
        return (status === 'SCHEDULED' || status === 'BOOKED') && isFuture;
      })
      .map(l => ({
        ...l,
        displayType: 'lesson',
        startTime: l.dateTime,
        cost: paymentDetails?.lessons?.find(pl => String(pl.id) === String(l.id))?.cost || 0,
        currency: paymentDetails?.currency || 'EUR',
        isUpcoming: true
      }));

    const scheduledExams = (paymentDetails?.exams || [])
      .filter(e => new Date(e.startTime).getTime() > now)
      .map(e => ({
        ...e,
        displayType: 'exam',
        isUpcoming: true
      }));

    const upcoming = [...scheduledLessons, ...scheduledExams]
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    // 2. Get history items (Past)
    const historyLessons = lessons
      .filter(l => {
        const status = String(l.status || '').toUpperCase();
        const isPast = l.dateTime ? new Date(l.dateTime).getTime() <= now : false;
        return (status === 'SCHEDULED' || status === 'BOOKED' || status === 'COMPLETED') && isPast;
      })
      .map(l => ({
        ...l,
        displayType: 'lesson',
        startTime: l.dateTime,
        cost: paymentDetails?.lessons?.find(pl => String(pl.id) === String(l.id))?.cost || 0,
        currency: paymentDetails?.currency || 'EUR',
        isUpcoming: false
      }));

    const historyExams = (paymentDetails?.exams || [])
      .filter(e => new Date(e.startTime).getTime() <= now)
      .map(e => ({
        ...e,
        displayType: 'exam',
        studentRating: e.studentRating,
        isUpcoming: false
      }));

    const history = [...historyLessons, ...historyExams]
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

    return { upcoming, history };
  }, [lessons, paymentDetails]);

  const load = React.useCallback(async ()=>{
    if (!studentId) return;
    setLoading(true); setError(null);
    try {
      // Load the student info via teacher-scoped endpoint
      const u = await apiService.get<any>(API_ENDPOINTS.TEACHER.STUDENT_DETAIL(studentId));
      const data = (u && (u as any).data && typeof (u as any).data === 'object') ? (u as any).data : (u || null);
      setStudent(data);

      // Load this student's lessons for the current teacher
      const lessonsResp = await apiService.get<any>(`/api/teacher/students/${studentId}/lessons`);
      const list = Array.isArray((lessonsResp as any)?.data) ? (lessonsResp as any).data : (Array.isArray(lessonsResp) ? lessonsResp : []);
      const mapped = list.map((l:any)=>({
        id: l.id,
        studentId: l.student ? l.student.id : (l.studentId ?? null),
        studentName: l.studentName ?? (l.student ? `${l.student.firstName ?? ''} ${l.student.lastName ?? ''}`.trim() : undefined),
        dateTime: l.lessonDateTime ?? l.dateTime,
        duration: l.durationMinutes ?? l.duration,
        status: (typeof l.isBooked === 'boolean') ? (l.isBooked ? 'SCHEDULED' : 'AVAILABLE') : (l.status ?? 'SCHEDULED'),
        type: l.lessonType ?? l.type,
        teacherNote: l.teacherNote,
        studentRating: l.studentRating,
      }));
      const own = mapped.sort((a:any,b:any)=>{
        const ta = a?.dateTime ? Date.parse(a.dateTime) : Number.MAX_SAFE_INTEGER;
        const tb = b?.dateTime ? Date.parse(b.dateTime) : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      });
      setLessons(own);

      dispatch(fetchStudentPaymentDetailsForTeacher(studentId));
    } catch (e:any) {
      setError(e?.message || 'Failed to load data');
    } finally { setLoading(false); }
  }, [studentId]);

  React.useEffect(()=>{ load(); }, [load]);

  const sameAmsterdamDay = (isoA?: string, isoB?: string) => {
    if (!isoA || !isoB) return false;
    try {
      const a = new Date(isoA).toLocaleDateString('nl-NL',{timeZone:'Europe/Amsterdam'});
      const b = new Date(isoB).toLocaleDateString('nl-NL',{timeZone:'Europe/Amsterdam'});
      return a===b;
    } catch { return isoA.slice(0,10)===isoB.slice(0,10); }
  };
  const formatFriendlyDateAmsterdam = (iso?: string) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const wd = d.toLocaleString('en-GB',{weekday:'short', timeZone:'Europe/Amsterdam'});
      const day = d.toLocaleString('en-GB',{day:'2-digit', timeZone:'Europe/Amsterdam'});
      const mon = d.toLocaleString('en-GB',{month:'short', timeZone:'Europe/Amsterdam'});
      return `${wd} ${day} ${mon}`;
    } catch {
      const d = new Date(iso);
      const wd = d.toLocaleString('en-GB',{weekday:'short'});
      const day = d.toLocaleString('en-GB',{day:'2-digit'});
      const mon = d.toLocaleString('en-GB',{month:'short'});
      return `${wd} ${day} ${mon}`;
    }
  };

  const title = student ? `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim() || student.username || student.email : 'Student Details';

  return (
    <PageWrapper>
      <Navigation />
      <ContentWrapper>
        <ContentCard>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>Loading student details…</div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#b00020' }}>{error}</div>
          ) : !student ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>Student not found.</div>
          ) : (
            <>
              <HeaderSection>
                <ProfileCard>
                  <ProfileAvatarWrapper>
                    <Avatar 
                      size={100} 
                      src={`${API_BASE_URL}/api/users/${studentId}/avatar`}
                      initials={title[0].toUpperCase()} 
                      rounded={true}
                    />
                  </ProfileAvatarWrapper>
                  <ProfileText>
                    <h2 className="name">{title}</h2>
                    {student?.email && <p className="email">{student.email}</p>}
                  </ProfileText>
                </ProfileCard>

                <InfoGrid>
                  <InfoCard>
                    <div className="icon-wrapper"><LocationOnIcon sx={{ fontSize: 18 }} /></div>
                    <div className="content">
                      <div className="label">{t('portal.profile.address', 'Address')}</div>
                      <div className="value">{`${student.postcode || ''} ${student.houseNumber || ''}`.trim() || '—'}</div>
                    </div>
                  </InfoCard>
                  <InfoCard>
                    <div className="icon-wrapper"><PhoneIcon sx={{ fontSize: 18 }} /></div>
                    <div className="content">
                      <div className="label">Phone Number</div>
                      <div className="value">{(student.phoneNumber || student.phone) || '—'}</div>
                    </div>
                  </InfoCard>
                  <InfoCard>
                    <div className="icon-wrapper"><CakeIcon sx={{ fontSize: 18 }} /></div>
                    <div className="content">
                      <div className="label">Date of Birth</div>
                      <div className="value">{student.dateOfBirth || '—'}</div>
                    </div>
                  </InfoCard>
                  <InfoCard>
                    <div className="icon-wrapper"><ContactPhoneIcon sx={{ fontSize: 18 }} /></div>
                    <div className="content">
                      <div className="label">Emergency Contact</div>
                      <div className="value">{student.emergencyContact || '—'}</div>
                    </div>
                  </InfoCard>
                </InfoGrid>
              </HeaderSection>

              {paymentsLoading ? (
                <div style={{ padding: '1rem', textAlign: 'center' }}>{t('portal.common.loading', 'Loading…')}</div>
              ) : !paymentDetails ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#888' }}>{t('portal.student.dashboard.no_payments', 'No payment details available.')}</div>
              ) : (
                <>
                  <TableTitle>
                    {t('portal.student.payments.items_title', 'Lessons & Exams')}
                    <span className="count">{unifiedList.upcoming.length + unifiedList.history.length}</span>
                  </TableTitle>

                  {(paymentDetails.pendingPayments || []).length > 0 && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#111827', marginBottom: '1rem' }}>Pending Proof Submissions</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {paymentDetails.pendingPayments.map((p: any) => (
                          <div key={`pending-${p.id}`} style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b', padding: '1rem', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            <div className="info" style={{ flex: 1 }}>
                              <div style={{ fontWeight: 800, color: '#111827' }}>{p.description || 'Student recorded payment'}</div>
                              <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 }}>
                                {new Date(p.createdAt).toLocaleDateString()} • {p.proofDocument ? 'Receipt Attached' : 'No Receipt'}
                              </div>
                            </div>
                            <div className="hide-mobile">
                              {p.proofDocument && (
                                <a 
                                  href={`${API_BASE_URL}/api/payments/${p.id}/receipt`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: '#6366f1', fontWeight: 700, fontSize: '0.8rem', textDecoration: 'none' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View Receipt
                                </a>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleVerifyPayment(String(p.id), PaymentStatus.VERIFIED); }}
                                disabled={verifyingPayment === String(p.id)}
                                style={{
                                  background: '#10b981', color: 'white', border: 'none', borderRadius: 8, padding: '0.4rem 0.8rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.75rem'
                                }}
                              >
                                {verifyingPayment === String(p.id) ? '...' : 'Approve'}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleVerifyPayment(String(p.id), PaymentStatus.REJECTED); }}
                                disabled={verifyingPayment === String(p.id)}
                                style={{
                                  background: '#ef4444', color: 'white', border: 'none', borderRadius: 8, padding: '0.4rem 0.8rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.75rem'
                                }}
                              >
                                {verifyingPayment === String(p.id) ? '...' : 'Reject'}
                              </button>
                            </div>
                            <div style={{ textAlign: 'right', fontWeight: 800, color: '#d97706', fontSize: '0.9rem', minWidth: '80px' }}>
                              {formatCurrency(p.amount, paymentDetails.currency)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ padding: '0 0.5rem' }}>
                    {unifiedList.upcoming.length === 0 && unifiedList.history.length === 0 ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#fff' }}>
                        {t('portal.student.payments.no_items', 'No lessons or exams recorded.')}
                      </div>
                    ) : (
                      <div style={{ maxHeight: '800px', overflowY: 'auto' }}>
                        {/* Upcoming Section */}
                        {unifiedList.upcoming.map((item: any) => (
                          <AdvancedLessonCard
                            key={`${item.displayType}-${item.id}`}
                            lesson={{
                              ...item,
                              dateTime: item.startTime,
                              studentName: paymentDetails.studentName,
                              type: item.displayType,
                              status: new Date(item.startTime) > new Date() ? 'SCHEDULED' : 'COMPLETED'
                            }}
                            mode={new Date(item.startTime) > new Date() ? 'booked' : 'passed'}
                            onClick={() => {
                              if (item.displayType === 'lesson') {
                                navigate(`/portal/lesson/${item.id}`);
                              } else if (item.displayType === 'exam') {
                                navigate(`/portal/teacher/exams/${item.id}`);
                              }
                            }}
                          />
                        ))}

                        {/* History Section */}
                        {unifiedList.history.map((item: any) => (
                          <AdvancedLessonCard
                            key={`history-${item.displayType}-${item.id}`}
                            lesson={{
                              ...item,
                              dateTime: item.startTime,
                              studentName: paymentDetails.studentName,
                              type: item.displayType,
                              status: 'COMPLETED'
                            }}
                            mode="passed"
                            onClick={() => {
                              if (item.displayType === 'lesson') {
                                navigate(`/portal/lesson/${item.id}`);
                              } else if (item.displayType === 'exam') {
                                navigate(`/portal/teacher/exams/${item.id}`);
                              }
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <SummaryContainer>
                    <SummaryCard $type="total">
                      <div className="icon-wrapper">
                        <PaymentsIcon fontSize="small" />
                      </div>
                      <div className="content">
                        <span className="label">Total Amount</span>
                        <span className="value">{formatCurrency(paymentDetails.totalDue, paymentDetails.currency)}</span>
                      </div>
                    </SummaryCard>
                    <SummaryCard $type="paid">
                      <div className="icon-wrapper">
                        <CheckCircleIcon fontSize="small" />
                      </div>
                      <div className="content">
                        <span className="label">Total Paid</span>
                        <span className="value">{formatCurrency(paymentDetails.totalPaid, paymentDetails.currency)}</span>
                      </div>
                    </SummaryCard>
                    <SummaryCard $type="remaining">
                      <div className="icon-wrapper">
                        <TrendingUpIcon fontSize="small" />
                      </div>
                      <div className="content">
                        <span className="label">Remaining</span>
                        <span className="value">{formatCurrency(paymentDetails.totalDue - paymentDetails.totalPaid, paymentDetails.currency)}</span>
                      </div>
                    </SummaryCard>
                  </SummaryContainer>
                </>
              )}


            </>
          )}
        </ContentCard>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default TeacherStudentDetail;
